import React from "react";
import { useLocation, useNavigate } from "react-router";
import backButtonIcon from "../../assets/images/back-button.png";

const sectionTitleClass = "font-oswald text-[20px] font-bold text-white pt-5";
const subSectionTitleClass =
  "font-oswald text-[17px] font-semibold text-white pt-2";
const paragraphClass =
  "font-oswald text-[16px] font-normal leading-6 text-white/90";
const bulletListClass = "list-disc pl-8";
const bulletItemClass =
  "font-oswald text-[16px] font-normal leading-6 text-white/90";

export const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const shouldHideHeader =
    new URLSearchParams(location.search).get("hideHeader") === "1";

  const handleClose = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/auth");
  };

  return (
    <div className="flex min-h-dvh flex-col bg-xcf-gradient">
      {!shouldHideHeader && (
        <div className="sticky top-0 z-20 flex h-12 items-center justify-center bg-xcf-gradient-start px-3">
          <div className="relative mx-auto flex h-12 w-full max-w-4xl items-center justify-center">
            <button
              type="button"
              onClick={handleClose}
              className="absolute left-0 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center"
              aria-label="Back"
            >
              <img
                src={backButtonIcon}
                alt="Back"
                className="h-6 w-6 object-contain"
              />
            </button>
            <h1 className="font-oswald text-[18px] font-normal lowercase text-white">
              privacy policy
            </h1>
          </div>
        </div>
      )}

      <div className="mx-auto flex w-full max-w-4xl flex-1 overflow-y-auto px-4 pb-10">
        <div className="w-full space-y-5 text-left">
          <h1 className="py-4 text-center font-oswald text-[22px] font-normal text-white">
            Privacy Policy - XConfessions App
          </h1>

          <p className={paragraphClass}>Effective date: March 19, 2026</p>

          <p className={paragraphClass}>
            This Privacy Policy explains how LUST PRODUCTIONS, S.L. ("LUST,"
            "we," "us," or "our") collects, uses, discloses, and protects
            personal information in connection with the XConfessions mobile
            application and related services (the "App" or "Service").
          </p>

          <section className="space-y-2">
            <h2 className={subSectionTitleClass}>Data Controller</h2>
            <p className={paragraphClass}>LUST PRODUCTIONS, S.L.</p>
            <p className={paragraphClass}>C/ Diputació 185, Ppal 2</p>
            <p className={paragraphClass}>08011 Barcelona, Spain</p>
            <p className={paragraphClass}>Email: support@erikalust.com</p>
          </section>

          <p className={paragraphClass}>
            By accessing or using the App, you acknowledge that you have read
            this Privacy Policy.
          </p>

          <section className="space-y-2">
            <h2 className={sectionTitleClass}>1. Scope</h2>
            <p className={paragraphClass}>
              This Privacy Policy applies to personal information collected
              through the App, related customer support communications, and
              other interactions you may have with us in connection with the
              Service.
            </p>
            <p className={paragraphClass}>
              The App is intended only for adults. It is not directed to
              children under 18, and we do not knowingly collect personal
              information from children under 18.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className={sectionTitleClass}>2. Information We Collect</h2>
            <p className={paragraphClass}>
              Depending on how you use the App, we may collect the following
              categories of personal information:
            </p>

            <h3 className={subSectionTitleClass}>
              a. Information you provide directly
            </h3>
            <ul className={bulletListClass}>
              <li className={bulletItemClass}>
                Email address when you create an account, contact us, or choose
                to receive communications from us
              </li>
              <li className={bulletItemClass}>
                Profile photo(s) that you upload for profile-picture purposes
              </li>
              <li className={bulletItemClass}>
                Information you provide in messages to us or through support
                requests
              </li>
              <li className={bulletItemClass}>
                Any other information you voluntarily submit through the App
              </li>
            </ul>

            <h3 className={subSectionTitleClass}>
              b. Information collected automatically
            </h3>
            <p className={paragraphClass}>
              When you use the App, we may automatically collect certain
              technical and usage information, such as:
            </p>
            <ul className={bulletListClass}>
              <li className={bulletItemClass}>device type</li>
              <li className={bulletItemClass}>operating system</li>
              <li className={bulletItemClass}>app version</li>
              <li className={bulletItemClass}>
                approximate usage and interaction data
              </li>
              <li className={bulletItemClass}>
                crash, diagnostics, and performance-related information
              </li>
              <li className={bulletItemClass}>
                app event data and analytics information collected through
                Google Analytics for Firebase
              </li>
            </ul>
            <p className={paragraphClass}>
              Google states that Firebase Analytics automatically collects
              certain event and usage data and also provides controls over
              analytics data collection and use.
            </p>

            <h3 className={subSectionTitleClass}>
              c. Payment and transaction information
            </h3>
            <p className={paragraphClass}>
              If you purchase paid features, payment processing is handled by
              third-party providers, such as:
            </p>
            <ul className={bulletListClass}>
              <li className={bulletItemClass}>
                Apple App Store In-App Purchases
              </li>
              <li className={bulletItemClass}>SegPay</li>
            </ul>
            <p className={paragraphClass}>
              We do not store full payment card details when payments are
              processed by these third parties. We may receive limited
              transaction-related information, such as confirmation that a
              purchase was completed, a transaction identifier, product type,
              country, or related billing status information, as made available
              by the payment provider.
            </p>
            <p className={paragraphClass}>
              For SegPay transactions, SegPay's own privacy policy applies to
              the personal data it processes.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className={sectionTitleClass}>
              3. How We Use Personal Information
            </h2>
            <p className={paragraphClass}>
              We may use personal information for the following purposes:
            </p>
            <ul className={bulletListClass}>
              <li className={bulletItemClass}>
                to create and manage user accounts
              </li>
              <li className={bulletItemClass}>
                to provide and operate the App and its features
              </li>
              <li className={bulletItemClass}>
                to display and manage profile pictures
              </li>
              <li className={bulletItemClass}>
                to process or verify purchases and premium access
              </li>
              <li className={bulletItemClass}>
                to communicate with you about your account, purchases, support
                requests, or important service notices
              </li>
              <li className={bulletItemClass}>
                to send you promotional or marketing emails only where permitted
                by law and according to your choices
              </li>
              <li className={bulletItemClass}>
                to allow you to opt in or opt out of marketing communications
              </li>
              <li className={bulletItemClass}>
                to analyze usage, improve performance, troubleshoot issues, and
                develop new features
              </li>
              <li className={bulletItemClass}>
                to protect the security and integrity of the App
              </li>
              <li className={bulletItemClass}>
                to comply with legal obligations and enforce our Terms
              </li>
            </ul>
          </section>

          <section className="space-y-2">
            <h2 className={sectionTitleClass}>
              4. Legal Bases for Processing (GDPR)
            </h2>
            <p className={paragraphClass}>
              If the GDPR applies to our processing of your personal data, we
              rely on one or more of the following legal bases:
            </p>
            <ul className={bulletListClass}>
              <li className={bulletItemClass}>
                Performance of a contract: where processing is necessary to
                provide the App and related services to you
              </li>
              <li className={bulletItemClass}>
                Consent: where you have given us consent, such as for optional
                marketing emails or where consent is otherwise required
              </li>
              <li className={bulletItemClass}>
                Legitimate interests: where processing is necessary for our
                legitimate interests, such as improving the App, preventing
                fraud, maintaining security, and understanding user engagement,
                provided those interests are not overridden by your rights
              </li>
              <li className={bulletItemClass}>
                Legal obligation: where processing is necessary for compliance
                with applicable law
              </li>
            </ul>
            <p className={paragraphClass}>
              Where we rely on consent, you may withdraw it at any time.
              Withdrawal does not affect the lawfulness of processing carried
              out before withdrawal.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className={sectionTitleClass}>5. Profile Photos</h2>
            <p className={paragraphClass}>
              If you upload a photo to use as a profile picture, we will process
              that photo for profile display and account-related purposes within
              the App. You are responsible for ensuring that you have the right
              to upload any image you submit and that it does not violate
              applicable law or the rights of others.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className={sectionTitleClass}>6. Analytics</h2>
            <p className={paragraphClass}>
              We use Google Analytics for Firebase to help us understand how
              users interact with the App, measure app performance, and improve
              the Service. This may include collection of app-instance data,
              device-related data, event data, and similar analytics
              information.
            </p>
            <p className={paragraphClass}>
              We do not use remarketing services, and we do not use your
              personal information for cross-context behavioral advertising or
              similar targeted advertising activities. We also state that we do
              not show ads in the App. Google's Firebase documentation describes
              Analytics as a service for understanding how users use Apple,
              Android, and web apps, and explains that analytics collection can
              be configured by the developer.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className={sectionTitleClass}>7. Marketing Communications</h2>
            <p className={paragraphClass}>
              We may send emails to users who have opted in to receive marketing
              or promotional communications, where applicable law permits.
            </p>
            <p className={paragraphClass}>
              You can opt out of marketing emails at any time by:
            </p>
            <ul className={bulletListClass}>
              <li className={bulletItemClass}>
                using the unsubscribe link included in the message, where
                available
              </li>
              <li className={bulletItemClass}>
                changing your settings in the App, where available
              </li>
              <li className={bulletItemClass}>
                contacting us at support@erikalust.com
              </li>
            </ul>
            <p className={paragraphClass}>
              Even if you opt out of marketing emails, we may still send you
              non-promotional communications, such as transactional messages,
              account notices, purchase confirmations, and important updates
              about the Service.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className={sectionTitleClass}>
              8. How We Share Personal Information
            </h2>
            <p className={paragraphClass}>
              We may share personal information with:
            </p>
            <ul className={bulletListClass}>
              <li className={bulletItemClass}>
                service providers and vendors that help us operate the App, such
                as hosting, analytics, customer support, and technical service
                providers
              </li>
              <li className={bulletItemClass}>
                payment processors and platform providers, such as Apple and
                SegPay, to enable purchases and payment-related operations
              </li>
              <li className={bulletItemClass}>
                professional advisors, auditors, or legal counsel where
                reasonably necessary
              </li>
              <li className={bulletItemClass}>
                competent authorities, courts, regulators, or law enforcement
                where required by law or to protect rights, safety, and security
              </li>
              <li className={bulletItemClass}>
                a buyer, investor, or successor entity in connection with a
                merger, acquisition, financing, corporate reorganization, or
                sale of assets
              </li>
            </ul>
            <p className={paragraphClass}>
              We do not sell personal information.
            </p>
            <p className={paragraphClass}>
              We do not share personal information for behavioral advertising or
              remarketing purposes.
            </p>
            <p className={paragraphClass}>
              Under California law, consumers may have rights related to the
              sale or sharing of personal information; because we do not do
              remarketing or targeted advertising and do not show ads, this
              policy reflects a "no sale / no sharing for cross-context
              behavioral advertising" position.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className={sectionTitleClass}>9. Data Retention</h2>
            <p className={paragraphClass}>
              We retain personal information only for as long as reasonably
              necessary for the purposes described in this Privacy Policy,
              including:
            </p>
            <ul className={bulletListClass}>
              <li className={bulletItemClass}>maintaining your account</li>
              <li className={bulletItemClass}>providing the Service</li>
              <li className={bulletItemClass}>
                complying with legal, accounting, tax, or regulatory obligations
              </li>
              <li className={bulletItemClass}>resolving disputes</li>
              <li className={bulletItemClass}>enforcing our agreements</li>
            </ul>
            <p className={paragraphClass}>
              Retention periods may vary depending on the type of data and the
              reason we collected it.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className={sectionTitleClass}>
              10. International Data Transfers
            </h2>
            <p className={paragraphClass}>
              Because some of our service providers may operate outside your
              country, your personal information may be transferred to and
              processed in countries other than the one in which you reside,
              including countries outside the European Economic Area.
            </p>
            <p className={paragraphClass}>
              Where required by applicable law, we will take appropriate steps
              to ensure that such transfers are subject to adequate safeguards,
              such as contractual protections or other lawful transfer
              mechanisms.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className={sectionTitleClass}>11. Security</h2>
            <p className={paragraphClass}>
              We implement reasonable technical and organizational measures
              designed to protect personal information against accidental or
              unlawful destruction, loss, alteration, unauthorized disclosure,
              or access.
            </p>
            <p className={paragraphClass}>
              However, no method of transmission or storage is completely
              secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className={sectionTitleClass}>
              12. Your Rights Under the GDPR
            </h2>
            <p className={paragraphClass}>
              If you are located in the European Economic Area, United Kingdom,
              or another jurisdiction with similar rights, you may have the
              right to:
            </p>
            <ul className={bulletListClass}>
              <li className={bulletItemClass}>
                request access to your personal data
              </li>
              <li className={bulletItemClass}>
                request correction of inaccurate or incomplete data
              </li>
              <li className={bulletItemClass}>request deletion of your data</li>
              <li className={bulletItemClass}>
                request restriction of processing
              </li>
              <li className={bulletItemClass}>object to certain processing</li>
              <li className={bulletItemClass}>request data portability</li>
              <li className={bulletItemClass}>
                withdraw consent where processing is based on consent
              </li>
              <li className={bulletItemClass}>
                lodge a complaint with a competent supervisory authority
              </li>
            </ul>
            <p className={paragraphClass}>
              To exercise these rights, contact us at support@erikalust.com.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className={sectionTitleClass}>
              13. California Privacy Rights (CCPA/CPRA Notice)
            </h2>
            <p className={paragraphClass}>
              If you are a California resident, and to the extent the CCPA as
              amended by the CPRA applies, you may have the following rights,
              subject to exceptions:
            </p>
            <ul className={bulletListClass}>
              <li className={bulletItemClass}>
                the right to know the categories of personal information we
                collect, use, disclose, and retain
              </li>
              <li className={bulletItemClass}>
                the right to request access to specific pieces of personal
                information
              </li>
              <li className={bulletItemClass}>
                the right to request deletion of personal information
              </li>
              <li className={bulletItemClass}>
                the right to request correction of inaccurate personal
                information
              </li>
              <li className={bulletItemClass}>
                the right to know the categories of sources from which personal
                information is collected
              </li>
              <li className={bulletItemClass}>
                the right to know the business or commercial purposes for
                collecting, using, or disclosing personal information
              </li>
              <li className={bulletItemClass}>
                the right to know the categories of third parties to whom
                personal information is disclosed
              </li>
              <li className={bulletItemClass}>
                the right to opt out of the sale or sharing of personal
                information, if applicable
              </li>
              <li className={bulletItemClass}>
                the right to limit the use and disclosure of sensitive personal
                information, where applicable
              </li>
              <li className={bulletItemClass}>
                the right not to receive discriminatory treatment for exercising
                your privacy rights
              </li>
            </ul>
            <p className={paragraphClass}>
              We do not sell personal information, and we do not share personal
              information for cross-context behavioral advertising.
            </p>
            <p className={paragraphClass}>
              To submit a California privacy request, contact us at
              support@erikalust.com. We may need to verify your identity before
              responding to your request.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className={sectionTitleClass}>14. CalOPPA Notice</h2>
            <p className={paragraphClass}>
              In accordance with the California Online Privacy Protection Act
              ("CalOPPA"), this Privacy Policy provides notice of:
            </p>
            <ul className={bulletListClass}>
              <li className={bulletItemClass}>
                the categories of personal information we collect
              </li>
              <li className={bulletItemClass}>
                the categories of third parties with whom we may share personal
                information
              </li>
              <li className={bulletItemClass}>
                how users can request changes to their personal information
              </li>
              <li className={bulletItemClass}>
                how we will notify users of material changes to this Privacy
                Policy
              </li>
            </ul>
            <p className={paragraphClass}>
              California's online privacy rules require operators of online
              services to conspicuously post a privacy policy and include
              certain disclosures.
            </p>

            <h3 className={subSectionTitleClass}>Do Not Track</h3>
            <p className={paragraphClass}>
              Some web browsers may transmit "Do Not Track" ("DNT") signals.
              Because there is no universally accepted standard for how to
              interpret DNT signals across apps and services, our App does not
              currently respond to browser-based DNT signals.
            </p>
            <p className={paragraphClass}>
              However, we do not use the App to engage in third-party behavioral
              advertising or remarketing.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className={sectionTitleClass}>15. Children's Privacy</h2>
            <p className={paragraphClass}>
              The App is intended for adults only. We do not knowingly collect
              personal information from children under the age of 18. If we
              learn that we have collected personal information from a child
              under 18 without appropriate authorization, we will take steps to
              delete that information.
            </p>
            <p className={paragraphClass}>
              If you believe that a child under 18 has provided us with personal
              information, please contact us at support@erikalust.com.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className={sectionTitleClass}>16. Third-Party Services</h2>
            <p className={paragraphClass}>
              The App may integrate with or rely on third-party services,
              including app stores, analytics providers, hosting providers, and
              payment processors. Those third parties may process personal data
              in accordance with their own privacy notices and terms.
            </p>
            <p className={paragraphClass}>This includes, where applicable:</p>
            <ul className={bulletListClass}>
              <li className={bulletItemClass}>
                Apple App Store / Apple In-App Purchases
              </li>
              <li className={bulletItemClass}>
                Google Firebase / Google Analytics for Firebase
              </li>
              <li className={bulletItemClass}>SegPay</li>
            </ul>
            <p className={paragraphClass}>
              We are not responsible for the privacy practices of third parties
              except as required by applicable law.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className={sectionTitleClass}>
              17. Changes to This Privacy Policy
            </h2>
            <p className={paragraphClass}>
              We may update this Privacy Policy from time to time. When we do,
              we will post the updated version in the App or through another
              appropriate channel and update the "Effective date" above.
            </p>
            <p className={paragraphClass}>
              Your continued use of the App after the updated Privacy Policy
              becomes effective means that you acknowledge the updated policy,
              to the extent permitted by law.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className={sectionTitleClass}>18. Contact Us</h2>
            <p className={paragraphClass}>
              If you have questions about this Privacy Policy or want to
              exercise your privacy rights, contact us at:
            </p>
            <p className={paragraphClass}>LUST PRODUCTIONS, S.L.</p>
            <p className={paragraphClass}>C/ Diputació 185, Ppal 2</p>
            <p className={paragraphClass}>08011 Barcelona, Spain</p>
            <a
              href="mailto:support@erikalust.com"
              className="font-oswald text-[16px] font-normal text-white underline transition-opacity hover:opacity-80"
            >
              support@erikalust.com
            </a>
          </section>

          <section className="space-y-2">
            <h2 className={sectionTitleClass}>
              19. Digital Content and Withdrawal Right
            </h2>
            <p className={paragraphClass}>
              To the extent applicable under Spanish consumer regulations, the
              right of withdrawal may not apply to the supply of digital content
              not provided on a tangible medium once performance has begun with
              the consumer's prior express consent and acknowledgment that the
              right of withdrawal is thereby lost.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};
